import { type ChangeEvent, type FC, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { ArrowRight, Camera, Loader2, Pencil, MapPin } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { FieldError } from "@/shared/components/ui/FieldError";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { Relationship } from "@/shared/constants/relationshipOptions";
import { RelationshipSelect } from "@/pages/onboarding/components/RelationshipSelect";
import { usePersonPhotoUrl } from "@/shared/hooks/usePersonPhotoUrl";
import { usePersonPhotoUpload } from "@/shared/hooks/usePersonPhotoUpload";
import { usePerson } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { useUserRelationship } from "@/shared/hooks/useUserRelationship";
import { cn, EventType, setPersonProperties, trackEvent } from "@/shared/utils/core";
import { getEditedFields, getOnboardingFlow } from "@/shared/utils/funnelAnalytics";
import { getFirstName, buildFullName, extractLastName } from "@/shared/utils/names";
import { formatPersonDateForDisplay } from "@/shared/utils/formatters";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";
import { validateDecedentDates } from "@/shared/utils/validators";

export const ConfirmDetails: FC<FlowCustomComponentProps> = ({
  onComplete,
  setAnswer,
  answers,
  goToScreen,
}) => {
  const person = usePerson();
  const estateId = person?.estate_id ?? null;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { photoUrl, isLoading: isPhotoLoading, refreshPhotoUrl } = usePersonPhotoUrl(estateId);
  const { uploadPersonPhoto, isUploading: isPhotoUploading } = usePersonPhotoUpload();
  const { updateRelationship } = useUserRelationship();
  const [editing, setEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<{ message: string; field: string } | null>(null);
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const nameFromAnswers =
    (answers["scraped-name"] as string) || (answers["decedent-name"] as string) || "";
  const middleNameFromAnswers = (answers["scraped-middle-name"] as string) || "";
  const dobFromAnswers =
    (answers["scraped-dob"] as string) || (answers["decedent-dob"] as string) || "";
  const dodFromAnswers =
    (answers["scraped-dod"] as string) || (answers["decedent-dod"] as string) || "";

  const initName = nameFromAnswers || person?.full_name || "";
  const nameParts = initName.split(" ");
  const initFirstName = nameParts[0] || "";
  const initMiddleName = middleNameFromAnswers || person?.middle_name || "";
  const initLastName = extractLastName(nameParts, initMiddleName);

  const [editFirstName, setEditFirstName] = useState(initFirstName);
  const [editMiddleName, setEditMiddleName] = useState(initMiddleName);
  const [editLastName, setEditLastName] = useState(initLastName);
  const [editDob, setEditDob] = useState(dobFromAnswers || person?.date_of_birth || "");
  const [editDod, setEditDod] = useState(dodFromAnswers || person?.date_of_death || "");

  const editName = buildFullName(editFirstName, editMiddleName, editLastName);

  useEffect(() => {
    const name = nameFromAnswers || person?.full_name || "";
    const parts = name.split(" ");
    const middle = middleNameFromAnswers || person?.middle_name || "";
    setEditFirstName(parts[0] || "");
    setEditMiddleName(middle);
    setEditLastName(extractLastName(parts, middle));
    setEditDob(dobFromAnswers || person?.date_of_birth || "");
    setEditDod(dodFromAnswers || person?.date_of_death || "");
    setImgError(false);
    setImgLoaded(false);
  }, [
    nameFromAnswers,
    middleNameFromAnswers,
    dobFromAnswers,
    dodFromAnswers,
    person?.full_name,
    person?.middle_name,
    person?.date_of_birth,
    person?.date_of_death,
  ]);

  const scrapedImageUrl = (answers["scraped-image-url"] as string) || null;
  const resolvedPhotoUrl = imgError ? null : scrapedImageUrl || photoUrl;
  const hasNoPhoto = !isPhotoLoading && !resolvedPhotoUrl;
  const showSkeleton = !hasNoPhoto && (!resolvedPhotoUrl || !imgLoaded);

  const fullName = editName || person?.full_name || "Unknown";
  const firstName = getFirstName(fullName || undefined);
  const relationship = (answers["decedent-relationship"] as string) || "";

  const dob = editDob || person?.date_of_birth;
  const dod = editDod || person?.date_of_death;
  const dateOfBirthFormatted = formatPersonDateForDisplay(
    dob,
    { year: "numeric", month: "long", day: "numeric" },
    "",
  );
  const dateOfDeathFormatted = formatPersonDateForDisplay(
    dod,
    { year: "numeric", month: "long", day: "numeric" },
    "",
  );
  const dateRange =
    dateOfBirthFormatted && dateOfDeathFormatted
      ? `${dateOfBirthFormatted}  -  ${dateOfDeathFormatted}`
      : dateOfBirthFormatted || null;

  const residence = (answers["scraped-residence"] as string) || person?.location || null;

  const handlePhotoSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !estateId) return;
    const fileKey = await uploadPersonPhoto(file, estateId);
    if (fileKey) {
      setAnswer("scraped-image-url", "");
      setImgError(false);
      setImgLoaded(false);
      await refreshPhotoUrl();
    } else {
      enqueueSnackbar("Couldn't update the photo. Please try again.", { variant: "error" });
    }
  };

  const fieldError = (field: string) =>
    dateError?.field === field ? "border-destructive border-[2px]" : "";

  const validateDates = (dobValue: string, dodValue: string) =>
    validateDecedentDates(dobValue || undefined, dodValue || undefined, "edit-dob", "edit-dod");

  const handleSaveDetails = () => {
    const dateValidation = validateDates(editDob, editDod);
    setDateError(dateValidation);
    if (!dateValidation) {
      setEditing(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const dateValidation = validateDates(editDob, editDod);
    if (dateValidation) {
      setEditing(true);
      setDateError(dateValidation);
      return;
    }

    if (!relationship) {
      setError(`Choose your relationship to ${firstName}`);
      return;
    }

    setAnswer("decedent-name", editName);
    setAnswer("scraped-name", editName);
    setAnswer("scraped-middle-name", editMiddleName);
    setAnswer("decedent-dob", editDob);
    setAnswer("decedent-dod", editDod);

    setIsSubmitting(true);
    setError(null);

    try {
      if (estateId) {
        await supabase
          .from("estates")
          .update({
            full_name: editName,
            first_name: editFirstName,
            middle_name: editMiddleName || null,
            last_name: editLastName,
          })
          .eq("estate_id", estateId);
      }

      await updateRelationship(relationship);

      if (relationship === Relationship.Attorney) {
        setIsSubmitting(false);
        goToScreen("attorney-redirect");
        return;
      }

      const fieldsEdited = getEditedFields(
        {
          first_name: initFirstName,
          middle_name: initMiddleName,
          last_name: initLastName,
          dob: dobFromAnswers || person?.date_of_birth,
          dod: dodFromAnswers || person?.date_of_death,
        },
        {
          first_name: editFirstName,
          middle_name: editMiddleName,
          last_name: editLastName,
          dob: editDob,
          dod: editDod,
        },
      );
      trackEvent(EventType.DECEDENT_DETAILS_SAVED, {
        flow: getOnboardingFlow(answers),
        estate_id: estateId != null ? String(estateId) : undefined,
        decedent_path: "obituary",
        relationship,
        fields_edited: fieldsEdited.join(","),
        edited_field_count: fieldsEdited.length,
        has_dob: Boolean(editDob),
        has_dod: Boolean(editDod),
      });
      setPersonProperties({}, { decedent_path: "obituary" });

      onComplete();
    } catch (err) {
      setError("Your details weren't saved. Try again.");
      sentryUtils.captureError(err, "ConfirmDetails submission failed", {
        estateId: estateId ?? undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <h2>Does this look right?</h2>
        <p className="mt-3 text-sm text-secondary">
          The basics are filled in from the obituary. Fix anything that's off, since these facts
          start the estate record.
        </p>
      </div>

      <div className="relative overflow-hidden rounded-lg border border-primary bg-white">
        <div className="relative flex flex-col items-center gap-2 px-6 py-8">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-background" />
            {resolvedPhotoUrl && (
              <img
                src={resolvedPhotoUrl}
                alt=""
                className={cn(
                  "absolute inset-0 h-full w-full object-cover opacity-10 transition-opacity duration-500",
                  imgLoaded ? "opacity-10" : "opacity-0",
                )}
              />
            )}
          </div>
          <p className="relative font-serif text-sm text-secondary">In memory</p>
          <Button
            type="button"
            variant="outline"
            aria-label={hasNoPhoto ? "Add photo" : "Change photo"}
            disabled={isPhotoUploading}
            onClick={() => fileInputRef.current?.click()}
            className="group relative h-[143px] w-[143px] shrink-0 overflow-hidden rounded-lg p-1 shadow-sm hover:bg-white disabled:bg-white"
          >
            {showSkeleton && (
              <div className="flex h-full w-full items-center justify-center rounded-md bg-stone-200 animate-pulse">
                <Loader2 className="h-6 w-6 animate-spin text-secondary" />
              </div>
            )}
            {resolvedPhotoUrl && (
              <img
                src={resolvedPhotoUrl}
                alt={fullName}
                className={cn(
                  "absolute inset-0 h-full w-full rounded-md object-cover transition-opacity duration-500",
                  imgLoaded ? "opacity-100" : "opacity-0",
                )}
                onLoad={() => setImgLoaded(true)}
                onError={() => setImgError(true)}
              />
            )}
            {resolvedPhotoUrl && !isPhotoUploading && (
              <span className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50 opacity-0 transition-opacity [@media(hover:hover)]:group-hover:opacity-100">
                <Camera className="h-6 w-6 text-white" />
              </span>
            )}
            {(hasNoPhoto || isPhotoUploading) && (
              <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                {isPhotoUploading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-secondary" />
                ) : (
                  <Camera className="h-6 w-6 text-tertiary" />
                )}
              </span>
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoSelect}
          />
        </div>

        {editing ? (
          <div className="flex flex-col gap-4 px-6 pb-6 pt-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-first-name">First name</Label>
                <Input
                  id="edit-first-name"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-middle-name">Middle name</Label>
                <Input
                  id="edit-middle-name"
                  value={editMiddleName}
                  onChange={(e) => setEditMiddleName(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-last-name">Last name</Label>
              <Input
                id="edit-last-name"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-dob">Date of birth</Label>
                <DatePicker
                  id="edit-dob"
                  dateString={editDob || undefined}
                  onChange={() => {}}
                  onDateStringChange={(val) => {
                    setEditDob(val || "");
                    setDateError(null);
                  }}
                  placeholder="Pick a date"
                  className={fieldError("edit-dob")}
                />
                {dateError?.field === "edit-dob" && (
                  <FieldError message={dateError.message} className="mt-1" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-dod">Date of passing</Label>
                <DatePicker
                  id="edit-dod"
                  dateString={editDod || undefined}
                  onChange={() => {}}
                  onDateStringChange={(val) => {
                    setEditDod(val || "");
                    setDateError(null);
                  }}
                  placeholder="Pick a date"
                  className={fieldError("edit-dod")}
                />
                {dateError?.field === "edit-dod" && (
                  <FieldError message={dateError.message} className="mt-1" />
                )}
              </div>
            </div>
            <RelationshipSelect
              name={firstName}
              value={relationship}
              onChange={(value) => setAnswer("decedent-relationship", value)}
            />
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDateError(null);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="brand" onClick={handleSaveDetails}>
                Save details
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-8">
            <h2>{fullName}</h2>
            {dateRange && <p className="text-secondary">{dateRange}</p>}
            {residence && (
              <p className="flex items-center gap-1.5 text-sm text-secondary">
                <MapPin className="h-4 w-4" />
                {residence}
              </p>
            )}
          </div>
        )}

        {!editing && (
          <>
            <Button
              variant="outline"
              size="icon"
              className="absolute right-6 top-6 z-10"
              onClick={() => setEditing(true)}
              aria-label="Edit details"
            >
              <Pencil className="h-4 w-4" />
            </Button>

            <div className="border-t border-primary px-6 py-6">
              <RelationshipSelect
                name={firstName}
                value={relationship}
                onChange={(value) => {
                  setAnswer("decedent-relationship", value);
                  if (error) setError(null);
                }}
                error={error || undefined}
              />
            </div>
          </>
        )}
      </div>

      {!editing && (
        <Button
          variant="brand"
          rounded
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              {relationship === Relationship.Attorney
                ? "Create an attorney account"
                : "This looks right"}{" "}
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      )}
    </div>
  );
};
